import React, { useEffect, useMemo, useRef, useState } from "react";
import { FormikProps } from "formik";
import XGSFormInput from "../../../../ui-components/form/input/xgsFormInput";
import XGSFormSelect from "../../../../ui-components/form/select/xgsFormSelect";
import XGSFormTextarea from "../../../../ui-components/form/textarea/xgsFormTextarea";
import { LabelModes } from "../../../../ui-components/molecules/labeled-inputs/labeledInput";
import { statusOptions, isEquipmentTypeWithoutVehicleIds } from "app/data/equipment/models";
import ProgressBar from "ui-components/progress-bar/progressBar";
import { ReactComponent as FileIcon } from "images/file-grey.svg";
import { ReactComponent as UploadFile } from "images/uploadfile-uparrow-gray.svg";
import {
    useDeleteEquipmentFile, useEquipmentTypes, useUploadEquipmentFile,
    useProductLines, useAssetDescriptor, useLeaseTypes, useMakes, useTerminalsList
} from "hooks/useEquipment";
import { Popover } from "ui-components/popover/popover";
import { toast } from "react-toastify";
import { ReactComponent as AlertIcon } from "images/alert.svg";
import ConfirmationModal from "ui-components/confirmation-modal/confirmationModal";

type DocumentFileType = 'REGISTRATION' | 'DOT_INSPECTION';

interface EquipmentFormFieldsProps {
    formikProps: FormikProps<any>;
    isEdit: boolean;
    CardComponent: React.ElementType<{ title: string; hideButtons: boolean; mix?: string; children: React.ReactNode; }>;
    initialData?: { unitNumber?: string; utilizationPercent?: number, id?: string, registrationDocUrl?: string, dotCertificateUrl?: string };
    handleSave: (payload: any, files: { [key in DocumentFileType]?: File }) => void;
}

interface DocumentUploadFieldProps {
    label: string;
    fileType: DocumentFileType;
    handleUpload: (fileType: DocumentFileType, file: File) => void;
    uploadInputRef: React.RefObject<HTMLInputElement>;
}

const DocumentUploadField: React.FC<DocumentUploadFieldProps> = ({ label, fileType, handleUpload, uploadInputRef }) => {

    const handleTriggerClick = (e: React.MouseEvent<HTMLDivElement>) => {
        uploadInputRef.current?.click();
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files.length > 0) {
            handleUpload(fileType, e.target.files[0]);
            e.target.value = '';
        }
    };

    return (
        <div className="document-upload-field" onClick={handleTriggerClick}>
            <div className="document-upload-field__left">
                <UploadFile className="document-upload-field__icon" />
                <div className="document-upload-field__text">
                    <span className="document-upload-field__label">{label}</span>
                    <span className="document-upload-field__subtext">Click to upload</span>
                </div>
            </div>
            <span className="document-upload-field__cta">Upload</span>
            <input
                type="file"
                ref={uploadInputRef}
                onChange={handleFileChange}
                style={{ display: 'none' }}
            />
        </div>
    );
};

const DocumentField: React.FC<{
    fileName: string,
    fileUrl: string,
    isEdit: boolean,
    onDelete: () => void,
    onReplace: (file: File) => void,
    replaceInputRef: React.RefObject<HTMLInputElement>,
    fileType: DocumentFileType,
    setReplaceTarget: React.Dispatch<React.SetStateAction<{
        type: DocumentFileType;
        file: File;
        ref: React.RefObject<HTMLInputElement>;
    } | null>>
}> = ({ fileName, fileUrl, isEdit, onDelete, onReplace, replaceInputRef, fileType, setReplaceTarget }) => {

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files.length > 0) {
            setReplaceTarget({
                type: fileType,
                file: e.target.files[0],
                ref: replaceInputRef
            });
        }
    };

    return (
        <div className="document-field">
            <div className="document-field__info">
                <FileIcon className="document-field__icon" />
                <span className="document-field__name">{fileName}</span>
            </div>
            <div className="document-field__actions">
                <a href={fileUrl} target="_blank" rel="noopener noreferrer" className="document-field__action-link">
                    View
                </a>
                {isEdit && (
                    <>
                        <span
                            role="button"
                            tabIndex={0}
                            className="document-field__action-link"
                            onClick={() => {
                                replaceInputRef.current?.click();
                            }}
                        >
                            Replace
                        </span>

                        <input
                            type="file"
                            ref={replaceInputRef}
                            onChange={handleFileChange}
                            style={{ display: 'none' }}
                        />

                        <span
                            role="button"
                            tabIndex={0}
                            className="document-field__action-link document-field__action-link--delete"
                            onClick={onDelete}
                        >
                            Delete
                        </span>
                    </>
                )}
            </div>
        </div>
    );
};

const EquipmentFormFields = ({ formikProps, isEdit, CardComponent, initialData, handleSave }: EquipmentFormFieldsProps): React.ReactElement => {

    const [localFiles, setLocalFiles] = useState<{ [key in DocumentFileType]?: File }>({});

    const { mutateAsync: uploadFile } = useUploadEquipmentFile();
    const { mutateAsync: deleteFile } = useDeleteEquipmentFile();
    const [replaceTarget, setReplaceTarget] = useState<{
        type: DocumentFileType;
        ref: React.RefObject<HTMLInputElement>;
    } | null>(null);
    const [deleteTarget, setDeleteTarget] = useState<DocumentFileType | null>(null);

    const registrationInputRef = useRef<HTMLInputElement>(null);
    const dotInputRef = useRef<HTMLInputElement>(null);

    const equipmentId = initialData?.id;

    const handleFileChange = (fileType: DocumentFileType, file: File) => {
        const fieldName = fileType === 'REGISTRATION' ? 'registrationDocUrl' : 'dotCertificateUrl';
        const fileTypeKey = fileType === 'REGISTRATION' ? 'Registration Document' : 'DOT Inspection Certificate';

        if (isEdit && equipmentId) {
            handleEditUpload(fileType, file);
        } else {
            setLocalFiles(prev => ({ ...prev, [fileType]: file }));

            formikProps.setFieldValue(fieldName, file.name);
            formikProps.setFieldTouched(fieldName, true, false);
            toast.success(`${fileTypeKey}: File selected locally for upload.`);
        }
    };

    const handleEditUpload = async (fileType: DocumentFileType, file: File) => {
        if (!equipmentId) return toast.error("Cannot perform file operation: Equipment ID missing.");

        const fieldName = fileType === 'REGISTRATION' ? 'registrationDocUrl' : 'dotCertificateUrl';
        const fileTypeKey = fileType === 'REGISTRATION' ? 'Registration Document' : 'DOT Inspection Certificate';

        try {
            const response = await uploadFile({ equipmentId, fileType, file });
            const newFileUrl = response?.data?.url || response?.url;

            if (newFileUrl) {
                formikProps.setFieldValue(fieldName, newFileUrl);
                formikProps.setFieldTouched(fieldName, true, false);
                toast.success(`${fileTypeKey} uploaded successfully!`);
            } else {
                toast.error(`File uploaded but URL not received.`);
            }
        } catch (error: any) {
            toast.error(error.message || `Failed to upload/replace ${fileTypeKey}.`);
        }
    }

    const handleDelete = async (fileType: DocumentFileType) => {
        const fieldName = fileType === 'REGISTRATION' ? 'registrationDocUrl' : 'dotCertificateUrl';
        const fileTypeKey = fileType === 'REGISTRATION' ? 'Registration Document' : 'DOT Inspection Certificate';

        if (isEdit && equipmentId) {
            try {
                await deleteFile({ equipmentId, fileType });
                formikProps.setFieldValue(fieldName, null);
                formikProps.setFieldTouched(fieldName, true, false);
                toast.success(`${fileTypeKey} deleted successfully!`);
            } catch (error: any) {
                toast.error(error.message || `Failed to delete ${fileTypeKey}.`);
            }
        } else {
            setLocalFiles(prev => {
                const newState = { ...prev };
                delete newState[fileType];
                return newState;
            });
            formikProps.setFieldValue(fieldName, null);
            formikProps.setFieldTouched(fieldName, true, false);
            toast.success(`${fileTypeKey} removed locally.`);
        }
    };

    useEffect(() => {
        if (!isEdit) {
            handleSave(formikProps.values, localFiles);
        }
    }, [formikProps.values, localFiles, isEdit, handleSave]);

    const { data: equipmentTypeOptions = [] } = useEquipmentTypes();
    const { data: productLineOptions = [] } = useProductLines();
    const { data: assetDescriptorOptions = [] } = useAssetDescriptor();
    const { data: leaseTypeOptions = [] } = useLeaseTypes();
    const { data: makeOptions = [] } = useMakes();
    const { data: terminalOptions = [] } = useTerminalsList();

    const getOption = (options: any[], value: string | number | undefined | null, key: string = 'value') => {
        if (value === null || value === undefined || value === "") {
            return undefined;
        }
        const lowerCaseValue = String(value).toLowerCase();

        const found = options.find((option) =>
            option[key] && String(option[key]).toLowerCase() === lowerCaseValue
        );

        if (found) {
            return found;
        }

        // Support creatable / custom values not yet in the options list
        return { value: String(value), label: String(value) };
    };

    const setCreatableFieldValue = (fieldName: string, option?: { value?: string | boolean | null } | null) => {
        const nextValue = option?.value === null || option?.value === undefined
            ? ""
            : String(option.value).trim();
        formikProps.setFieldValue(fieldName, nextValue);
    };

    const hideVehicleIdFields = isEquipmentTypeWithoutVehicleIds(formikProps.values.equipmentType);

    const uploadedDocuments = useMemo(() => {
        const docs: Array<{ name: string, url: string, fileType: DocumentFileType, ref: React.RefObject<HTMLInputElement> }> = [];

        if (localFiles.REGISTRATION) {
            docs.push({
                name: localFiles.REGISTRATION.name,
                url: localFiles.REGISTRATION.name,
                fileType: 'REGISTRATION',
                ref: registrationInputRef
            });
        } else if (formikProps.values.registrationDocUrl) {
            docs.push({
                name: "Registration Document",
                url: formikProps.values.registrationDocUrl,
                fileType: 'REGISTRATION',
                ref: registrationInputRef
            });
        }

        if (localFiles.DOT_INSPECTION) {
            docs.push({
                name: localFiles.DOT_INSPECTION.name,
                url: localFiles.DOT_INSPECTION.name,
                fileType: 'DOT_INSPECTION',
                ref: dotInputRef
            });
        } else if (formikProps.values.dotCertificateUrl) {
            docs.push({
                name: "DOT Inspection Certificate",
                url: formikProps.values.dotCertificateUrl,
                fileType: 'DOT_INSPECTION',
                ref: dotInputRef
            });
        }

        return docs;
    }, [formikProps.values.registrationDocUrl, formikProps.values.dotCertificateUrl, localFiles]);

    const utilizationPercent = isEdit ? (initialData?.utilizationPercent ?? 0) : 0;

    const handleFileChangeFromModal = (fileType: DocumentFileType, file: File) => {
        handleFileChange(fileType, file);
    };

    return (
        <>
            <div className="equipment-add-edit-section-container">
                <CardComponent title="" hideButtons={true} mix="euipment-info-card">
                    <XGSFormInput
                        type="text"
                        name="unitNumber"
                        labelMode={LabelModes.column}
                        label="Unit No."
                        required
                        readonly={isEdit}
                    />
                    <XGSFormSelect
                        label="Type"
                        name="equipmentType"
                        required
                        creatable
                        className="xgs-equipment-creatable-select"
                        options={equipmentTypeOptions}
                        value={getOption(equipmentTypeOptions, formikProps.values.equipmentType)}
                        onValueChange={(option) => setCreatableFieldValue("equipmentType", option)}
                        formik={formikProps}
                        labelMode={LabelModes.column}
                    />
                    <XGSFormSelect
                        label={
                            <>
                                Asset Descriptor{" "}
                                <Popover
                                    values={
                                        <div className="popover-value">
                                            Describes the asset configuration, like Sleeper, Straight Truck, or TADC (Tandem Axle).
                                        </div>
                                    }
                                />
                            </>
                        }
                        name="assetDescriptor"
                        creatable
                        className="xgs-equipment-creatable-select"
                        options={assetDescriptorOptions}
                        value={getOption(assetDescriptorOptions, formikProps.values.assetDescriptor)}
                        onValueChange={(option) => setCreatableFieldValue("assetDescriptor", option)}
                        formik={formikProps}
                        labelMode={LabelModes.column}
                    />
                    <XGSFormSelect
                        label="Make"
                        name="make"
                        creatable
                        className="xgs-equipment-creatable-select"
                        options={makeOptions}
                        value={getOption(makeOptions, formikProps.values.make)}
                        onValueChange={(option) => setCreatableFieldValue("make", option)}
                        formik={formikProps}
                        labelMode={LabelModes.column}
                    />
                    <XGSFormInput
                        type="text"
                        name="model"
                        labelMode={LabelModes.column}
                        label="Model"
                    />
                    <XGSFormInput
                        type="text"
                        name="year"
                        labelMode={LabelModes.column}
                        label={
                            <>
                                Year{" "}
                                <Popover
                                    values={
                                        <div className="popover-value">
                                            Model year of the equipment.
                                        </div>
                                    }
                                />
                            </>
                        }
                    />
                    {!hideVehicleIdFields && (
                        <XGSFormInput
                            type="text"
                            name="vin"
                            labelMode={LabelModes.column}
                            label={
                                <>
                                    VIN (Vehicle ID){" "}
                                    <Popover
                                        values={
                                            <div className="popover-value">
                                                Vehicle Identification Number.
                                            </div>
                                        }
                                    />
                                </>}
                        />
                    )}
                </CardComponent>

                <CardComponent title="Registration & Compliance" hideButtons={true} mix="registration-id-card">
                    {!hideVehicleIdFields && (
                        <XGSFormInput
                            type="text"
                            name="licensePlate"
                            labelMode={LabelModes.column}
                            label="License Plate"
                        />
                    )}
                    <XGSFormInput
                        type="date"
                        name="registrationDate"
                        labelMode={LabelModes.column}
                        label="Registered on"
                        max={new Date().toISOString().split("T")[0]}
                    />
                    {!hideVehicleIdFields && (
                        <XGSFormInput
                            type="date"
                            name="dotInspectionDate"
                            labelMode={LabelModes.column}
                            label={
                                <>
                                    DOT Inspection Date{" "}
                                    <Popover
                                        values={
                                            <div className="popover-value">
                                                Next scheduled Department of Transportation inspection date.
                                            </div>
                                        }
                                    />
                                </>
                            }
                        />
                    )}
                    <div />
                </CardComponent>

                {hideVehicleIdFields && (
                    <CardComponent title="Asset Assignment" hideButtons={true} mix="asset-assignment-card">
                        <XGSFormInput
                            type="text"
                            name="assetTag"
                            labelMode={LabelModes.column}
                            label="Asset Tag"
                        />
                        <XGSFormInput
                            type="text"
                            name="employeeAssigned"
                            labelMode={LabelModes.column}
                            label="Employee Assigned"
                        />
                        <XGSFormInput
                            type="date"
                            name="assignedDate"
                            labelMode={LabelModes.column}
                            label="Assigned Date"
                        />
                        <XGSFormInput
                            type="date"
                            name="purchaseDate"
                            labelMode={LabelModes.column}
                            label="Purchase Date"
                        />
                        <XGSFormInput
                            type="datetime-local"
                            name="lastCheckIn"
                            labelMode={LabelModes.column}
                            label="Last Check-In"
                        />
                    </CardComponent>
                )}

                {isEdit && (
                    <CardComponent title="Assignment & Utilization" hideButtons={true} mix="assignment-utilization-card">
                        <XGSFormSelect
                            label={
                                <>
                                    Terminal{" "}
                                    <Popover
                                        values={
                                            <div className="popover-value">
                                                Terminal or hub where this equipment is currently assigned.
                                            </div>
                                        }
                                    />
                                </>
                            }
                            name="location"
                            required
                            disabled={true}
                            options={terminalOptions}
                            value={getOption(terminalOptions, formikProps.values.location)}
                            onValueChange={(option) => {
                                formikProps.setFieldValue("location", option?.value ?? "");
                            }}
                            formik={formikProps}
                            labelMode={LabelModes.column}
                        />
                        <XGSFormSelect
                            label="Service Status"
                            name="status"
                            options={statusOptions}
                            value={getOption(statusOptions, formikProps.values.status)}
                            onValueChange={(option) => {
                                formikProps.setFieldValue("status", option?.value ?? "");
                            }}
                            formik={formikProps}
                            labelMode={LabelModes.column}
                        />
                        {!hideVehicleIdFields && (
                            <div className="utilization-row">
                                <span className="utilization-row__label">Utilization Time</span>
                                <div className="utilization-row__progress-bar">
                                    <ProgressBar value={utilizationPercent} />
                                </div>
                                <div className="utilization-row__indicators">
                                    <span className="low">Low 0-29%</span>
                                    <span className="moderate">Moderate 30-59%</span>
                                    <span className="healthy">Healthy 60-100%</span>
                                    <span>{utilizationPercent}%</span>
                                </div>
                            </div>
                        )}
                    </CardComponent>
                )}

                {!isEdit && (
                    <CardComponent title="Assignment Details" hideButtons={true} mix="assignment-details-card">
                        <XGSFormSelect
                            label="Terminal"
                            name="location"
                            required
                            disabled={true}
                            options={terminalOptions}
                            value={getOption(terminalOptions, formikProps.values.location)}
                            onValueChange={(option) => {
                                formikProps.setFieldValue("location", option?.value ?? "");
                            }}
                            formik={formikProps}
                            labelMode={LabelModes.column}
                        />
                        <XGSFormSelect
                            label="Service Status"
                            name="status"
                            options={statusOptions}
                            value={getOption(statusOptions, formikProps.values.status)}
                            onValueChange={(option) => {
                                formikProps.setFieldValue("status", option?.value ?? "");
                            }}
                            formik={formikProps}
                            labelMode={LabelModes.column}
                        />
                        <div />
                    </CardComponent>
                )}

                <CardComponent title="Ownership" hideButtons={true} mix="ownership-card">
                    <XGSFormSelect
                        label="Lease Type"
                        name="leaseType"
                        options={leaseTypeOptions}
                        value={getOption(leaseTypeOptions, formikProps.values.leaseType)}
                        onValueChange={(option) => {
                            formikProps.setFieldValue("leaseType", option?.value ?? "");
                        }}
                        formik={formikProps}
                        labelMode={LabelModes.column}
                    />
                    <XGSFormInput
                        type="text"
                        name="vendor"
                        labelMode={LabelModes.column}
                        label="Vendor"
                    />
                    <XGSFormSelect
                        label="Product Line"
                        name="productLine"
                        options={productLineOptions}
                        value={getOption(productLineOptions, formikProps.values.productLine)}
                        onValueChange={(option) => {
                            formikProps.setFieldValue("productLine", option?.value ?? "");
                        }}
                        formik={formikProps}
                        labelMode={LabelModes.column}
                    />
                </CardComponent>

                <CardComponent title="Upload Documents" hideButtons={true} mix="document-card-layout">
                    <div className="document-card-layout__content">
                        {uploadedDocuments.find(d => d.fileType === 'REGISTRATION') ? (
                            <DocumentField
                                fileName={uploadedDocuments.find(d => d.fileType === 'REGISTRATION')!.name}
                                fileUrl={uploadedDocuments.find(d => d.fileType === 'REGISTRATION')!.url}
                                fileType="REGISTRATION"
                                isEdit={isEdit || !equipmentId}
                                onDelete={() => setDeleteTarget('REGISTRATION')}
                                onReplace={(file) => handleFileChange('REGISTRATION', file)}
                                replaceInputRef={registrationInputRef}
                                setReplaceTarget={setReplaceTarget}
                            />
                        ) : (
                            <DocumentUploadField
                                label="Registration Document"
                                fileType="REGISTRATION"
                                handleUpload={handleFileChange}
                                uploadInputRef={registrationInputRef}
                            />
                        )}
                        {!hideVehicleIdFields && (
                            uploadedDocuments.find(d => d.fileType === 'DOT_INSPECTION') ? (
                                <DocumentField
                                    fileName={uploadedDocuments.find(d => d.fileType === 'DOT_INSPECTION')!.name}
                                    fileUrl={uploadedDocuments.find(d => d.fileType === 'DOT_INSPECTION')!.url}
                                    fileType="DOT_INSPECTION"
                                    isEdit={isEdit || !equipmentId}
                                    onDelete={() => setDeleteTarget('DOT_INSPECTION')}
                                    onReplace={(file) => handleFileChange('DOT_INSPECTION', file)}
                                    replaceInputRef={dotInputRef}
                                    setReplaceTarget={setReplaceTarget}
                                />
                            ) : (
                                <DocumentUploadField
                                    label="DOT Inspection Certificate"
                                    fileType="DOT_INSPECTION"
                                    handleUpload={handleFileChange}
                                    uploadInputRef={dotInputRef}
                                />
                            )
                        )}
                    </div>
                </CardComponent >

                <CardComponent title="Notes" hideButtons={true} mix="simple-card-content">
                    <XGSFormTextarea
                        name="notes"
                        labelMode={LabelModes.column}
                        placeholder="Enter notes here..."
                    />
                </CardComponent>
            </div >
            <ConfirmationModal
                opened={!!deleteTarget}
                onCancel={() => setDeleteTarget(null)}
                onConfirm={() => {
                    if (deleteTarget) {
                        handleDelete(deleteTarget);
                        setDeleteTarget(null);
                    }
                }}
            >
                <div className="confirmation-modal__content">
                    <AlertIcon />
                    <div>
                        Are you sure you want to delete the<br />
                        <strong>
                            {deleteTarget === 'REGISTRATION' ? 'Registration Document' : 'DOT Inspection Certificate'}
                        </strong>
                        ?
                    </div>
                </div>
            </ConfirmationModal>
            <ConfirmationModal
                opened={!!replaceTarget}
                onCancel={() => setReplaceTarget(null)}
                onConfirm={() => {
                    if (replaceTarget?.file) {
                        handleFileChangeFromModal(replaceTarget.type, replaceTarget.file);
                    }
                    setReplaceTarget(null);
                }}
            >
                <div className="confirmation-modal__content">
                    <AlertIcon />
                    <div>
                        Are you sure you want to replace the<br />
                        <strong>
                            {replaceTarget?.type === "REGISTRATION"
                                ? "Registration Document"
                                : "DOT Inspection Certificate"}
                        </strong>
                        ?
                    </div>
                </div>
            </ConfirmationModal>


        </>
    );
}

export default EquipmentFormFields;
